import type {
	ContactAccountOut,
	ContactCandidateOut,
	ContactTier,
	PropertyContactWithDetails,
	PropertyContactsGrouped,
} from '@/client';
import { resolvedContactFields } from '@/components/services/model/serviceContacts';
import { contactFromCandidate } from '@/components/services/model/extractedProviderContact';
import { CORE_SERVICE_CATEGORY_CODES, CORE_SERVICE_CATEGORY_LABELS } from '@/utils/serviceCategoryUtils';

/** Providers with no trade of their own, gathered rather than left off the page. */
const UNCATEGORISED_TRADE_LABEL = 'Other services';
export const ALL_TRADES = 'all';
export const ANY_STANDING = 'any';

/**
 * The trade a scope names, for a candidate the pipeline never classified.
 *
 * `suggested_scope` is built as `<service label> - <systems> - <warranty>`, and the service label is
 * the humanised category where there is one. So when the category is missing, the segment before the
 * first dash is still the trade — a surveyor whose classification never landed says "Surveyor" here
 * and would otherwise be filed under "Other services" with everyone else unclassified.
 */
const serviceLabelFromScope = (scope: string | null | undefined) =>
	scope?.split(' - ')[0].trim() || UNCATEGORISED_TRADE_LABEL;

/**
 * How much a provider matters, which is the only thing this says.
 *
 * Access is a separate dimension and is not folded in. Connecting grants somebody access to the
 * property record — that is what the connect dialog asks about — and it is a different
 * question from whether they are the one to call. The two often travel together, and a household
 * that connects its builder usually would name that builder, but neither implies the other: a
 * connected accountant is not the roofer, and the roofer everyone rings may have no access at all.
 * Deriving significance from access would make the page silently answer one with the other.
 */
export type ProviderStanding = 'primary' | 'secondary';

const STANDING_LABELS: Record<ProviderStanding, string> = {
	primary: 'Key contacts',
	secondary: 'General',
};

const STANDING_ORDER: ProviderStanding[] = ['primary', 'secondary'];

/** An absent tier means secondary: the reconciler only names a primary when it is confident of one. */
const standingOf = (tier: ContactTier | null | undefined): ProviderStanding =>
	tier === 'primary' ? 'primary' : 'secondary';

/**
 * The tab a saved contact is drawn on. Both standings share Current, as its two sections; Historical
 * takes a contact out of both. `roster` is not returned: it is a view with no tab to reach it.
 */
export const contactTab = (contact: PropertyContactWithDetails): 'current' | 'historical' =>
	contact.historical === true ? 'historical' : 'current';

/** What a star press writes: the other tier, read through the same rule that draws the sections. */
export const toggledTier = (tier: ContactTier | null | undefined): ContactTier =>
	standingOf(tier) === 'primary' ? 'secondary' : 'primary';

export interface DirectoryProvider {
	/** Unique across both origins, which number their rows separately. */
	key: string;
	name: string;
	company: string | null;
	email: string | null;
	phone: string | null;
	scope: string | null;
	/**
	 * What the property knows about them, in prose.
	 *
	 * The field the pipeline actually writes a description into. `scope` is a job title far more often
	 * than a description, so this is the only text on a provider that says anything a trade does not.
	 */
	notes: string | null;
	/** When it joined the property directory. Extracted candidates do not expose this yet. */
	addedAt: string | null;
	/** Whether the account can open the property right now. Live access, so it can lapse on its own. */
	isConnected: boolean;
	/**
	 * The account this provider lets into the property, which is not the same as the contact's own
	 * address: an owner who connected a company through a person put the access on that person's card.
	 *
	 * Set once a card has ever been connected, and kept after a disconnect — `isConnected` is the
	 * live fact, this is who it is about.
	 */
	account: ContactAccountOut | null;
	/**
	 * True for a provider that exists only in the review queue, with no `property_contacts` row
	 * behind it — see `contactFromCandidate`. Views that offer a write, rather than a read, need to
	 * know which providers a write can reach.
	 */
	isExtracted: boolean;
	standing: ProviderStanding;
	tradeKey: string;
	tradeLabel: string;
	/** What the details panel opens with. */
	contact: PropertyContactWithDetails;
}

export interface DirectoryFacet {
	value: string;
	label: string;
	count: number;
}

/** Trades are matched across two sources that spell them differently — a code and free text. */
const normaliseTrade = (label: string) => label.trim().toLowerCase().replace(/[-_]+/g, ' ');

/**
 * A trade as a reader should see it, rather than as the wire spells it.
 *
 * A trade read off a document arrives as whatever the pipeline called it — `kitchen_manufacturer`,
 * `engineer` — and a category the household never created carries the same string as its label. Both
 * reach the page as the heading of a card, where an underscore reads as a fault rather than as data.
 *
 * Only the two faults are corrected. A label that already carries capitals is left exactly as it is,
 * so `Heat-pump technician` keeps its hyphen and its sentence case rather than being title-cased
 * into something the category list never said.
 */
export const humaniseTrade = (label: string) => {
	const spaced = label.trim().replace(/_+/g, ' ');
	if (spaced !== spaced.toLowerCase()) return spaced;
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * What a provider does, as the reader is to see it.
 *
 * The documents' scope while the provider is still a candidate, the category's label once saved:
 * only a candidate carries a scope, and its category is a code for filing rather than reading. The
 * trade label still classifies both, which is what groups, filters and matches them.
 */
export const providerServiceLine = (provider: Pick<DirectoryProvider, 'scope' | 'tradeLabel'>) =>
	provider.scope?.trim() || provider.tradeLabel;

const CORE_ORDER_BY_TRADE = new Map(
	CORE_SERVICE_CATEGORY_CODES.map((code, index) => [normaliseTrade(CORE_SERVICE_CATEGORY_LABELS[code]), index]),
);

/**
 * Where a trade sits in the page's order, by its code where one exists and by its words otherwise.
 *
 * The words matter as much as the code: a core trade whose only provider came out of a document has
 * no category object to carry a code, and dropping it to the end would put the household's
 * electrician below their pool technician.
 */
const tradeOrder = (label: string, code?: string) => {
	const byCode = code
		? CORE_SERVICE_CATEGORY_CODES.indexOf(code as (typeof CORE_SERVICE_CATEGORY_CODES)[number])
		: -1;
	if (byCode !== -1) return byCode;
	return CORE_ORDER_BY_TRADE.get(normaliseTrade(label)) ?? Number.MAX_SAFE_INTEGER;
};

interface OrderedProvider extends DirectoryProvider {
	tradeRank: number;
}

/** One provider before the trade it belongs to has been given a single spelling and a place. */
interface UnplacedProvider {
	tradeKey: string;
	rank: number;
	/** Built once the trade's canonical label is known, since both origins carry it on the record. */
	place: (tradeLabel: string, tradeRank: number) => OrderedProvider;
}

const placeContact = (contact: PropertyContactWithDetails, tradeKey: string, rank: number): UnplacedProvider => ({
	tradeKey,
	rank,
	place: (tradeLabel, tradeRank) => ({
		key: `contact:${contact.id}`,
		...resolvedContactFields(contact),
		// A saved contact has no scope: it is candidate context, and confirmation does not keep it.
		scope: null,
		notes: contact.notes ?? null,
		addedAt: contact.created_at,
		isConnected: contact.is_connected ?? false,
		account: contact.account ?? null,
		// The association's own value where the owner set one, else the registry's. A party is global,
		// so the two agree unless a correction is in flight.
		isExtracted: false,
		standing: standingOf(contact.tier),
		tradeKey,
		tradeLabel,
		tradeRank,
		contact,
	}),
});

/**
 * A candidate's scope with its leading trade spelled as the directory spells it.
 *
 * The pipeline often opens the scope with the category itself, sometimes as nothing more than the
 * code capitalised ("Council Bca" for `council_bca`). That lead is the trade, so it reads as the
 * trade's label; whatever follows " - " is the document's own detail and is kept as written.
 */
const scopeWithTradeLabel = (candidate: ContactCandidateOut, tradeLabel: string): string | null => {
	const scope = candidate.scope?.trim();
	if (!scope) return null;
	const [lead, ...detail] = scope.split(' - ');
	const leadTrade = normaliseTrade(lead);
	const isTrade = [candidate.category, tradeLabel].some((trade) => trade && normaliseTrade(trade) === leadTrade);
	return isTrade ? [tradeLabel, ...detail].join(' - ') : scope;
};

const placeCandidate = (candidate: ContactCandidateOut, tradeKey: string, rank: number): UnplacedProvider => ({
	tradeKey,
	rank,
	place: (tradeLabel, tradeRank) => ({
		key: `candidate:${candidate.id}`,
		name: candidate.name ?? candidate.company ?? 'Unnamed contact',
		company: candidate.name ? (candidate.company ?? null) : null,
		email: candidate.email ?? null,
		phone: candidate.phone ?? null,
		scope: scopeWithTradeLabel(candidate, tradeLabel),
		notes: candidate.notes ?? null,
		addedAt: null,
		isConnected: false,
		// A candidate has no `property_contacts` row, so there is nothing to hold a grant.
		account: null,
		isExtracted: true,
		standing: standingOf(candidate.tier),
		tradeKey,
		tradeLabel,
		tradeRank,
		contact: contactFromCandidate(candidate, tradeLabel, tradeKey),
	}),
});

/**
 * Trade first, then significance, then by name.
 *
 * Leading with the trade keeps the unfiltered list readable as the grouped view it replaces without
 * spending a heading on every trade; significance is what the eye should find at the top of each
 * run, and a name is only how the rest stay in a predictable order between visits. Access is not
 * part of it: ordering by who can see the property record would answer a question nobody asked.
 */
const byTradeThenStanding = (a: OrderedProvider, b: OrderedProvider) => {
	if (a.tradeRank !== b.tradeRank) return a.tradeRank - b.tradeRank;
	if (a.tradeKey !== b.tradeKey) return a.tradeLabel.localeCompare(b.tradeLabel);
	if (a.standing !== b.standing) return STANDING_ORDER.indexOf(a.standing) - STANDING_ORDER.indexOf(b.standing);
	return a.name.localeCompare(b.name);
};

/**
 * Every provider connected to a property, in one list, whoever put them there.
 *
 * Two sources reach this page shaped differently — a contact carries a category object, a provider
 * Bundle has read off a document carries its trade as free text — so a "Heat-pump technician" from a
 * service record and an "Electrician" already on the property are matched on the words alone. A
 * trade matching nothing known is kept as it was read rather than folded into "Other": what came off
 * the document is usually more specific than the category list, and losing it loses the one thing
 * that says what the provider does.
 *
 * A trade that both sides name gets one spelling, the category's, and every provider under it is
 * given that: the free text is whatever was written on an invoice, and left as it arrived it would
 * split one trade into two filter chips that look like a rendering fault.
 * */
export const buildProviderDirectory = (
	contactGroups: readonly PropertyContactsGrouped[],
	candidates: readonly ContactCandidateOut[],
	/**
	 * The property's role labels by code. A trade can arrive as a code or a lower-cased spelling of
	 * one (`council_bca`, "council bca"), on a candidate or as a saved category's own label; it reads
	 * as the role's label ("Council / BCA") rather than a spelled-out code.
	 */
	roleLabels?: ReadonlyMap<string, string>,
): DirectoryProvider[] => {
	const unplaced: UnplacedProvider[] = [];
	// Matched however it is spelled: code or label, any case, spaces or underscores.
	const roleLabelFor = new Map<string, string>();
	for (const [code, label] of roleLabels ?? []) {
		roleLabelFor.set(normaliseTrade(code), label);
		roleLabelFor.set(normaliseTrade(label), label);
	}
	const readable = (trade: string) => roleLabelFor.get(normaliseTrade(trade)) ?? trade;
	const labels = new Map<string, string>();
	const ranks = new Map<string, number>();

	const claimTrade = (label: string, rank: number, isCanonical: boolean) => {
		const key = normaliseTrade(label);
		if (isCanonical || !labels.has(key)) labels.set(key, humaniseTrade(label));
		ranks.set(key, Math.min(ranks.get(key) ?? Number.MAX_SAFE_INTEGER, rank));
		return key;
	};

	for (const group of contactGroups) {
		// An empty category claims no trade: it would add a filter chip counting nobody.
		if (group.contacts.length === 0) continue;

		const rank = tradeOrder(group.category.label, group.category.code);
		const label = roleLabelFor.get(normaliseTrade(group.category.code)) ?? readable(group.category.label);
		const key = claimTrade(label, rank, true);
		for (const contact of group.contacts) unplaced.push(placeContact(contact, key, rank));
	}

	for (const candidate of candidates) {
		const label = readable(candidate.category?.trim() || serviceLabelFromScope(candidate.scope));
		const rank = tradeOrder(label);
		unplaced.push(placeCandidate(candidate, claimTrade(label, rank, false), rank));
	}

	return unplaced
		.map((provider) =>
			provider.place(labels.get(provider.tradeKey) ?? '', ranks.get(provider.tradeKey) ?? provider.rank),
		)
		.sort(byTradeThenStanding);
};

/** What the provider views draw, so a search only hides one for a reason the reader can see. */
const searchText = (provider: DirectoryProvider) =>
	`${provider.name} ${provider.company ?? ''} ${provider.scope ?? ''} ${provider.email ?? ''} ${provider.phone ?? ''}`.toLowerCase();

/** Every provider, then each significance rung that is represented in this directory. */
export const getStandingFacets = (providers: readonly DirectoryProvider[]): DirectoryFacet[] => {
	const counts = new Map<ProviderStanding, number>();
	for (const provider of providers) counts.set(provider.standing, (counts.get(provider.standing) ?? 0) + 1);

	return [
		{ value: ANY_STANDING, label: 'Everyone', count: providers.length },
		...STANDING_ORDER.filter((standing) => counts.has(standing)).map((standing) => ({
			value: standing,
			label: STANDING_LABELS[standing],
			count: counts.get(standing) ?? 0,
		})),
	];
};

/** Every trade represented in the directory, retaining the directory's own stable order. */
export const getTradeFacets = (providers: readonly DirectoryProvider[]): DirectoryFacet[] => {
	const facets = new Map<string, DirectoryFacet>();

	for (const provider of providers) {
		const existing = facets.get(provider.tradeKey);
		if (existing) existing.count += 1;
		else facets.set(provider.tradeKey, { value: provider.tradeKey, label: provider.tradeLabel, count: 1 });
	}

	return [{ value: ALL_TRADES, label: 'All trades', count: providers.length }, ...facets.values()];
};

export interface ProviderFilters {
	trade?: string;
	standing?: string;
	/** Access, which narrows independently of the key split rather than as a rung of it. */
	connectedOnly?: boolean;
	query?: string;
}

export const filterProviders = (
	providers: readonly DirectoryProvider[],
	filters: ProviderFilters = {},
): DirectoryProvider[] => {
	const { trade = ALL_TRADES, standing = ANY_STANDING, connectedOnly = false, query = '' } = filters;
	const trimmed = query.trim().toLowerCase();
	if (trade === ALL_TRADES && standing === ANY_STANDING && !connectedOnly && !trimmed) {
		return providers as DirectoryProvider[];
	}

	return providers.filter(
		(provider) =>
			(trade === ALL_TRADES || provider.tradeKey === trade) &&
			(standing === ANY_STANDING || provider.standing === standing) &&
			(!connectedOnly || provider.isConnected) &&
			(!trimmed || searchText(provider).includes(trimmed)),
	);
};

/** How many providers hold access, for the control that narrows to them. */
export const countConnected = (providers: readonly DirectoryProvider[]) =>
	providers.reduce((total, provider) => total + (provider.isConnected ? 1 : 0), 0);

export type ProviderSortMode = 'name-asc' | 'name-desc' | 'added-desc' | 'added-asc' | 'manual-first' | 'bundle-first';

export const PROVIDER_SORT_OPTIONS = [
	{ value: 'name-asc', label: 'Name A–Z' },
	{ value: 'name-desc', label: 'Name Z–A' },
	{ value: 'added-desc', label: 'Recently added' },
	{ value: 'added-asc', label: 'Oldest added' },
	{ value: 'manual-first', label: 'Manually added first' },
	{ value: 'bundle-first', label: 'Found by Bundle first' },
] as const satisfies ReadonlyArray<{ value: ProviderSortMode; label: string }>;

const compareText = (left: string | null | undefined, right: string | null | undefined) =>
	(left ?? '').localeCompare(right ?? '', undefined, { sensitivity: 'base', numeric: true });

/** Missing values stay at the end in both directions instead of jumping sides when the order flips. */
const compareOptional = <T>(left: T | null, right: T | null, compare: (a: T, b: T) => number) => {
	if (left === null && right === null) return 0;
	if (left === null) return 1;
	if (right === null) return -1;
	return compare(left, right);
};

const isBundleFound = (provider: DirectoryProvider) => provider.contact.source !== 'manual';
const addedTimestamp = (provider: DirectoryProvider) => {
	if (!provider.addedAt) return null;
	const timestamp = Date.parse(provider.addedAt);
	return Number.isNaN(timestamp) ? null : timestamp;
};

export const sortProviders = (providers: readonly DirectoryProvider[], mode: ProviderSortMode): DirectoryProvider[] =>
	[...providers].sort((left, right) => {
		const byName = compareText(left.name, right.name);

		switch (mode) {
			case 'name-desc':
				return compareText(right.name, left.name);
			case 'added-desc':
				return compareOptional(addedTimestamp(left), addedTimestamp(right), (a, b) => b - a) || byName;
			case 'added-asc':
				return compareOptional(addedTimestamp(left), addedTimestamp(right), (a, b) => a - b) || byName;
			case 'manual-first':
				return Number(isBundleFound(left)) - Number(isBundleFound(right)) || byName;
			case 'bundle-first':
				return Number(isBundleFound(right)) - Number(isBundleFound(left)) || byName;
			case 'name-asc':
			default:
				return byName;
		}
	});

/** A job the four guarantee a place for, that nobody on the property covers yet. */
export interface UnfilledJob {
	key: string;
	label: string;
	categoryCode: string;
}

/**
 * One place in the key list: the person covering a job, or the job itself with nobody against it.
 *
 * A gap is an entry rather than an afterthought appended to the list, so a missing roofer sits
 * between the plumber and the electrician where the eye expects a roofer to be. A list that puts
 * every absence at the bottom makes the reader hold the four jobs in their head to notice one is
 * gone, which is exactly the work the list exists to save them.
 */
export type KeyEntry =
	| { kind: 'provider'; key: string; provider: DirectoryProvider }
	| { kind: 'gap'; key: string; job: UnfilledJob };

export interface KeyProvidersSplit {
	/** The key list in reading order: the core jobs first, each filled or not, then the rest. */
	entries: KeyEntry[];
	keyProviders: DirectoryProvider[];
	everyoneElse: DirectoryProvider[];
}

/**
 * The handful of providers a property is judged on, then everyone else.
 *
 * Membership is the association's `tier`, and nothing else. The reconciler proposes one and the
 * household can promote or demote a saved contact, so there is a stored answer to read — the page
 * used to guess at one by taking the head of each core trade, which said "the first plumber we
 * happen to hold" where what was wanted was "the plumber this household would ring".
 *
 * Access is deliberately not part of it. Connecting somebody grants them trusted access to the
 * property record, which is a different question from whether they are worth naming, and a page that
 * derived one from the other would quietly publish who has access under a heading about importance.
 *
 * A core job with nobody promoted to it comes back as a gap, after the providers rather than among
 * them, so the list reads as who this household has and then what it is missing.
 */
export const splitKeyProviders = (providers: readonly DirectoryProvider[]): KeyProvidersSplit => {
	const isKey = (provider: DirectoryProvider) => provider.standing === 'primary';

	const keyProviders = providers.filter(isKey);
	const byTrade = new Map<string, DirectoryProvider[]>();
	for (const provider of keyProviders) {
		const bucket = byTrade.get(provider.tradeKey);
		if (bucket) bucket.push(provider);
		else byTrade.set(provider.tradeKey, [provider]);
	}

	const entries: KeyEntry[] = [];
	const placed = new Set<string>();

	// The four jobs in their fixed order, each with whoever this household would ring for it. The
	// order never changes with the data, which is what makes the list readable at a glance.
	for (const code of CORE_SERVICE_CATEGORY_CODES) {
		const tradeKey = normaliseTrade(CORE_SERVICE_CATEGORY_LABELS[code]);
		const covering = byTrade.get(tradeKey);
		if (!covering) continue;

		placed.add(tradeKey);
		for (const provider of covering) entries.push({ kind: 'provider', key: provider.key, provider });
	}

	// Then anyone else the property counts as key, in the directory's own order.
	for (const provider of keyProviders) {
		if (placed.has(provider.tradeKey)) continue;
		entries.push({ kind: 'provider', key: provider.key, provider });
	}

	/**
	 * The jobs with nobody named for them, under the people rather than among them.
	 *
	 * A gap asks the household to name somebody, which is a different act from reading who they
	 * already have — and interleaved by trade order it pushed a prompt above the providers the list
	 * is for. The table has always drawn them this way.
	 *
	 * Held against the key providers, not against everyone in the trade. A secondary plumber does not
	 * answer "who would you ring", so a property with one and nobody promoted still has that job
	 * unnamed. This reverses the earlier rule, on the product owner's call.
	 */
	for (const code of CORE_SERVICE_CATEGORY_CODES) {
		const label = CORE_SERVICE_CATEGORY_LABELS[code];
		const tradeKey = normaliseTrade(label);
		if (byTrade.has(tradeKey)) continue;

		entries.push({ kind: 'gap', key: `gap:${tradeKey}`, job: { key: tradeKey, label, categoryCode: code } });
	}

	return { entries, keyProviders, everyoneElse: providers.filter((provider) => !isKey(provider)) };
};

/** The columns the provider table can be read down. */
export type ProviderSortColumn = 'name' | 'service' | 'phone' | 'email';

/**
 * What each column sorts on.
 *
 * Lowercased, because a table ordered by name should not put every company above every person on
 * the accident of a capital letter. An absent value sorts as empty rather than as the string
 * "null", which would file a contact with no phone under N.
 */
/**
 * The two lines the Provider column prints: the name, then the company, as the cards print them.
 *
 * A contact with no company, and a company's own card whose name repeats the company, print one line.
 */
export const providerTableLines = (provider: DirectoryProvider): { primary: string; secondary: string | null } => {
	const company = provider.company?.trim();
	const name = provider.name.trim();
	return { primary: name, secondary: company && company !== name ? company : null };
};

export const providerSortValue = (provider: DirectoryProvider, key: ProviderSortColumn): string => {
	const value =
		key === 'name'
			? providerTableLines(provider).primary
			: key === 'service'
				? providerServiceLine(provider)
				: key === 'phone'
					? provider.phone
					: provider.email;

	return value?.toLowerCase() ?? '';
};

/**
 * The `extracted_contacts` id behind a provider the pipeline found.
 *
 * `contactFromCandidate` carries it as a negative id, so a synthesised contact can never collide
 * with a real `property_contacts` row. Only meaningful where `isExtracted`.
 */
export const candidateIdOf = (provider: DirectoryProvider) => Math.abs(provider.contact.id);
